'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { trackPageView, isGAReady } from '../../lib/analytics';

/**
 * PageViewTracker - Automatically tracks page views on route changes
 * 
 * This component listens to pathname changes and sends page view events
 * to Google Analytics when the route changes.
 */
export default function PageViewTracker() {
  const pathname = usePathname();
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip initial mount (we'll track it after a small delay to ensure GA is ready)
    if (prevPathnameRef.current === null) {
      prevPathnameRef.current = pathname;
      
      // Track initial page view after a short delay to ensure GA is initialized
      // (in case user previously accepted cookies)
      const timer = setTimeout(() => {
        if (isGAReady()) {
          trackPageView();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }

    // Track page view when pathname changes
    if (pathname !== prevPathnameRef.current && isGAReady()) {
      trackPageView();
      prevPathnameRef.current = pathname;
    }
  }, [pathname]);

  // This component doesn't render anything
  return null;
}

